<template>
  <el-card shadow="never">
    <div>
      <div class="employees-header">
        <a class="employees-title">Employees</a>
        <el-button
          @click="addAccountItem"
          type="primary"
          icon="el-icon-plus"
          circle
        >
        </el-button>
      </div>
      <div class="employees-list" v-if="loading">
        <el-card shadow="never" v-for="i in 3" :key="i">
          <el-skeleton animated :style="{textAlign: 'left'}" />
        </el-card>
      </div>
      <div class="employees-list" v-else-if="customer.accounts && customer.accounts.length > 0">
        <div
          v-for="(account, index) of customer.accounts"
          :key="account._id"
          :style="{ background: 'white' }"
        >
          <el-row :gutter="10">
            <el-col>
              <el-card class="card" shadow="never">
                <div class="card-header" slot="header">
                  <div>
                    <el-tag v-if="account.access">
                      {{ account.access }}
                    </el-tag>
                    <el-tag
                      v-else
                      size="mini"
                      :key="type"
                      style="margin-right: 5px"
                      v-for="type of account.multiAccess"
                    >
                      {{ type }}
                    </el-tag>
                  </div>
                  <span style="float: right; padding-bottom: 10px">
                    <el-button
                      type="danger"
                      icon="el-icon-delete"
                      circle
                      @click="() => deleteContactItem(index)"
                      size="mini"
                    ></el-button>
                    <el-button
                      type="primary"
                      icon="el-icon-edit"
                      circle
                      @click="
                        () => loadExistingAccountForNewAccountDialog(account)
                      "
                      size="mini"
                    ></el-button>
                  </span>
                </div>
                <el-row :gutter="24">
                  <el-col :span="24">
                    <div>Name: {{ account.name }}</div>
                    <div>Email: {{ account.email }}</div>
                    <div>
                      Facilities:
                      {{
                        account.locations
                          .map((facilityId) => facilityLabelMap[facilityId])
                          .join(", ")
                      }}
                    </div>
                    <div v-if="account.phoneNumber">
                      Phone Number: {{ account.phoneNumber }}
                    </div>
                  </el-col>
                  <el-col :span="12">
                    <div
                      v-if="account.forkLift && account.forkLift.certified"
                      style="
                        float: right;
                        padding-bottom: 15px;
                        text-align: right;
                      "
                    >
                      <div>
                        <i class="el-icon-check icon-color"></i>
                        Forklift Certified
                      </div>
                      <div>
                        Instructor Name: {{ account.forkLift.instructorName }}
                      </div>
                      <div v-if="account.forkLift && account.forkLift.expiredAt">
                        Expiry Date: {{ account.forkLift.expiredAt | formatDate }}
                      </div>
                    </div>
                  </el-col>
                </el-row>
              </el-card>
            </el-col>
          </el-row>
        </div>
      </div>
      <NoSiteMap v-else description="No employees found"/>
    </div>
    <el-dialog
      id="accountItem"
      :visible.sync="newAccountItemDialog"
      @close="closeDialog"
    >
      <el-card v-loading="loading">
        <el-form :model="newAccount" ref="accountForm">
          <el-form-item
            label="Name"
            prop="name"
            :rules="[
              {
                required: true,
                message: 'Please input name',
                trigger: 'blur',
              },
            ]"
          >
            <el-input
              v-model="newAccount.name"
              :disabled="editingExistingAccount"
            >
            </el-input>
          </el-form-item>
          <el-form-item
            label="Email"
            prop="email"
            :rules="[
              {
                required: true,
                message: 'Please input email address',
                trigger: 'blur',
              },
              {
                type: 'email',
                message: 'Please input correct email address',
                trigger: ['blur', 'change'],
              },
            ]"
          >
            <el-input
              v-model="newAccount.email"
              :disabled="editingExistingAccount"
            >
            </el-input>
          </el-form-item>
          <el-form-item label="Phone Number" prop="phoneNumber">
            <el-input v-model="newAccount.phoneNumber"> </el-input>
          </el-form-item>
          <el-form-item
            prop="multiAccess"
            label="Email Notifications"
            :rules="[
              {
                required: true,
                message: 'At least one type of contact must be selected.',
                trigger: 'blur',
              },
            ]"
          >
            <el-select v-model="newAccount.multiAccess" multiple>
              <el-option value="WAREHOUSE"> </el-option>
              <el-option value="LEASING"> </el-option>
              <el-option value="FINANCE"> </el-option>
              <el-option value="LEGAL"> </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            prop="multiAccess"
            label="Facilities"
            :rules="formRules"
          >
            <el-select v-model="newAccount.locations" multiple>
              <el-option
                v-for="facility in customer.locations"
                :value="facility"
                :label="facilityLabelMap[facility]"
                :key="facility"
              >
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Forklift Certified">
            <el-checkbox v-model="newAccount.forkLift.certified"> </el-checkbox>
          </el-form-item>
          <el-form-item
            v-if="newAccount.forkLift.certified"
            label="Name of the Instructor"
          >
            <el-input v-model="newAccount.forkLift.instructorName"></el-input>
          </el-form-item>
          <el-form-item
            v-if="newAccount.forkLift.certified"
            label="Expiry Date"
          >
            <el-date-picker
              v-model="newAccount.forkLift.expiredAt"
              placeholder="Expiry Date"
              type="date"
              :picker-options="expiryEndDatePickerOptions"
            >
            </el-date-picker>
          </el-form-item>
          <el-button v-if="!editingExistingAccount" @click="saveAccountItem">
            Save
          </el-button>
          <el-button v-else @click="editAccountItem"> Edit </el-button>
        </el-form>
      </el-card>
    </el-dialog>
  </el-card>
</template>

<script>
import axios from 'axios';
import NoSiteMap from './NoSiteMap.vue';
import { ADMIN_API_BASE, CUSTOMER_API_BASE } from '../env';

export default {
  name: 'CustomerEmployees',
  props: {
    id: { type: String, required: true },
    facility: { type: String, required: true },
  },
  components: {
    NoSiteMap,
  },
  data() {
    const expiryEndDateValidator = date => date < new Date();
    return {
      facilityLabelMap: {},
      customer: {},
      loading: true,
      newAccountItemDialog: false,
      editingExistingAccount: false,
      formRules: [
        {
          required: true,
          message: 'At least one location must be selected.',
          trigger: 'blur',
        },
      ],
      newAccount: {
        createdBy: '',
        email: '',
        name: '',
        access: '',
        phoneNumber: '',
        multiAccess: [],
        forkLift: {
          instructorName: '',
          certified: false,
          expiredAt: null,
        },
      },
      expiryEndDatePickerOptions: {
        disabledDate: expiryEndDateValidator,
      },
    };
  },
  async mounted() {
    this.loading = true;
    await this.getCustomerDetails();
    await this.fetchFacilityLabels();
    this.loading = false;
  },
  watch: {
    async id() {
      try {
        await this.getCustomerDetails();
      } catch (err) {
        this.loading = false;
      }
    },
  },
  methods: {
    async fetchFacilityLabels() {
      const facilities = await axios.get(`${ADMIN_API_BASE}/facilities/`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
        params: { limit: 55 },
      });
      for (const facility of facilities.data) {
        this.facilityLabelMap[facility._id] = facility.title;
      }
    },
    async getCustomerDetails() {
      try {
        const customer = await axios.get(
          `${CUSTOMER_API_BASE}/customers/v1/${this.id}`,
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );
        this.customer = customer.data;
      } catch (err) {
        this.loading = false;
        if (err.response && err.response.data) {
          this.$message.error({
            title: 'Error',
            message: err.response.data,
          });
        }
      }
    },
    addAccountItem() {
      this.newAccount = {
        createdBy: this.$keycloak.tokenParsed.email,
        email: '',
        name: '',
        access: '',
        locations: this.customer.locations,
        forkLift: {
          instructorName: '',
          certified: false,
          expiredAt: null,
        },
      };
      this.newAccountItemDialog = true;
      this.loading = false;
    },
    async deleteContactItem(index) {
      this.loading = true;
      this.customer.accounts.splice(index, 1);
      await axios.put(
        `${CUSTOMER_API_BASE}/customers/v1/${this.id}`,
        {
          accounts: this.customer.accounts,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      this.loading = false;
      this.$message.success({
        message: 'Successfully deleted contact',
      });
    },
    async saveAccountItem() {
      try {
        this.loading = true;
        this.$refs.accountForm.validate(async (valid) => {
          if (valid) {
            if (!this.customer.accounts) {
              this.customer.accounts = [{ ...this.newAccount }];
            } else {
              this.customer.accounts.unshift({ ...this.newAccount });
            }
            const response = await axios.put(
              `${CUSTOMER_API_BASE}/customers/v1/${this.id}`,
              {
                accounts: this.customer.accounts,
              },
              {
                headers: {
                  Authorization: `Bearer ${this.$keycloak.token}`,
                },
              },
            );

            this.customer = response.data;
            this.loading = false;
            this.$message.success({
              title: 'Success',
              message: 'Account updated succesfully',
            });
            this.newAccountItemDialog = false;
            this.loading = false;
          }
          this.loading = false;
        });
      } catch (err) {
        this.loading = false;
        this.error = err.response.data;
      }
    },
    loadExistingAccountForNewAccountDialog(account) {
      this.editingExistingAccount = true;
      this.customer.accounts.forEach((item) => {
        if (item._id === account._id) {
          this.newAccount = { ...account };
          if (!account.forkLift) {
            this.newAccount.forkLift = {
              instructorName: '',
              certified: false,
              expiredAt: null,
            };
          }
        }
      });
      this.newAccountItemDialog = true;
      this.loading = false;
    },
    async editAccountItem() {
      this.loading = true;
      const accounts = this.customer.accounts.filter((item) => {
        if (item._id !== this.newAccount._id) {
          return item;
        }
      });

      accounts.unshift(this.newAccount);
      try {
        const response = await axios.put(
          `${CUSTOMER_API_BASE}/customers/v1/${this.id}`,
          {
            accounts,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );
        this.loading = false;
        this.customer = response.data;
        this.$notify.success({
          title: 'Success.',
          message: 'Account edited successfully!',
        });
      } catch (err) {
        this.loading = false;
        this.error = err.response.data;
      }
      this.newAccountItemDialog = false;
    },
    closeDialog() {
      this.loading = true;
      this.editingExistingAccount = false;
      this.newAccount = {
        createdBy: this.$keycloak.tokenParsed.email,
        email: '',
        name: '',
        access: '',
        forkLift: {
          certified: false,
        },
      };
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.employees-header {
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
}
.employees-title {
  font-weight: bolder;
}
.employees-list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
}
.card {
  padding: 10px;
  text-align: left;
}
.card-header {
  display: grid;
  grid-template-columns: 4fr 1fr;
}
.icon-color {
  color: #008000;
}
</style>



// WEBPACK FOOTER //
// src/components/CustomerEmployees.vue